<template>
  <fragment>
    <div ref="bar" v-bem>
      <div v-bem:container>
        <div v-bem:inner>
          <a v-bem:brand href="/">
            <img :src="logoUrl" />
          </a>
          <MainMenu v-if="$screen.lg" />
          <Burger />
        </div>
      </div>
      <div v-if="$screen.lg" v-bem:meta>
        <Meta />
      </div>
    </div>
    <div v-bem:placeholder />
  </fragment>
</template>

<script>
import Headroom from 'headroom.js';
import { mapState } from 'vuex';
import MainMenu from './desktop/MainMenu.vue';
import Meta from './Meta.vue';
import Burger from './mobile/Burger.vue';
import logo from '../assets/logo.svg';

export default {
  name: 'Bar',
  components: { Meta, MainMenu, Burger },
  computed: {
    ...mapState('pageHeader', ['isMobileOverlayOpen']),
    logoUrl() {
      if (BUILD_TARGET === 'app-drupal') {
        return `/themes/particle/dist/${logo}`;
      }
      return logo;
    },
  },
  watch: {
    isMobileOverlayOpen(isOpen) {
      // In Safari a scroll event might be triggered when opening the navigation
      // or clicking a link. This can trigger Headroom.js and the bar is hidden
      // although scrolling is still locked by body-scroll-lock. This leaves the
      // user in a state where scrolling is disabled and the overlay can't be
      // closed. The only way out is to reload. To fix this issue we freeze the
      // Headroom's current state (pinned, since there's no other way to open
      // the overlay) and unfreeze it as soon as the mobile overlay is closed.
      if (isOpen) {
        this.headroom.freeze();
      } else {
        this.headroom.unfreeze();
      }
    },
  },
  mounted() {
    this.headroom = new Headroom(this.$refs.bar, {
      offset: 100,
      classes: {
        pinned: 'bar--pinned',
        unpinned: 'bar--unpinned',
      },
    });

    this.headroom.init();
  },
};
</script>

<style lang="scss" scoped>
@use '~tokens/sass/utils';
@use 'sass:map';

%bar-height {
  height: utils.f-rem-calc(60);

  @include utils.bs-media-breakpoint-up(lg) {
    height: utils.f-rem-calc(84);
  }
}

.bar {
  @extend %bar-height;
  position: fixed;
  z-index: utils.$bs-zindex-fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  will-change: transform;
  transition: transform 200ms linear;

  @at-root #{&}__container {
    position: relative;
    z-index: 1;

    @include utils.bs-media-breakpoint-up(lg) {
      max-width: 1440px;
      margin: 0 auto;
      padding-left: utils.f-rem-calc(50);
    }
  }

  @at-root #{&}--pinned {
    transform: translateY(0%);
  }

  @at-root #{&}--unpinned {
    transform: translateY(-100%);
  }

  @at-root #{&}__placeholder {
    @extend %bar-height;
  }

  @at-root #{&}__inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: utils.f-rem-calc(10);

    @include utils.bs-media-breakpoint-up(lg) {
      justify-content: flex-start;
      align-items: flex-start;
      gap: utils.f-rem-calc(24);
      padding: 0;
    }
  }

  @at-root #{&}__brand {
    display: block;
    width: utils.f-convert-to-rem(136);
    margin-top: utils.f-convert-to-rem(-3);

    img {
      display: block;
      width: 100%;
      height: auto;
    }

    @include utils.bs-media-breakpoint-up(lg) {
      width: utils.f-rem-calc(170);
    }
  }

  @at-root #{&}__meta {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 2;
  }
}
</style>
